import type {
  DropsetDraft,
  SetIntensity,
  SetWork,
  WeightSource,
} from '@heraion/shared';

// The logger's SET: what the athlete types and what the plan prescribes on
// one set. Split out of loggerDraftTypes.ts for the 500-line cap (#336), at
// the seam between the set and the entry/draft shapes that hold it;
// loggerDraftTypes re-exports everything here.

// Per-set pill cycle: pending → done | pr → failed → pending.
// completedAt + fail + prFlash together encode the state, same shape
// the web's useSetCompletion hook uses, and what splits cleanly into
// the WorkoutSet payload at save time (prFlash is UI-only and is not
// persisted to the server). A failed set still keeps completedAt (it
// counted in volume, the lifter just missed the rep); returning to
// pending wipes all three.
export type SetState = 'pending' | 'done' | 'pr' | 'failed';

export type WeightUnit = 'kg' | 'lb';

export interface DraftSet {
  /** Free-text input, parsed to int on save. */
  reps: string;
  /** Free-text input, parsed to float on save. Display unit; converted
   *  to canonical kg via toCanonicalKg(weight, unit) on save. */
  weight: string;
  /** Per-set display unit; toggle independently. */
  unit: WeightUnit;
  /** Bodyweight-only sign for the EXTRA field. Android's decimal-pad
   *  doesn't expose a minus sign, so the negative path (band-assisted
   *  pull-ups, assisted-dip machine) needs an explicit toggle. The
   *  TextInput value stays positive; buildPayload multiplies by -1 at
   *  save time when this flag is set. Ignored on weighted entries. */
  extraNegative: boolean;
  /** ISO when the pill was first marked done; null while pending. */
  completedAt: string | null;
  /** True on the failed leg of the cycle; ignored when completedAt is null.
   *  A pure label (AP-0p model): `reps` already holds the clean reps done
   *  (the failed rep is decremented at fail time), so this changes no math. */
  fail: boolean;
  /** UI-only flag set at the pending→done transition when isSetPR
   *  returns true against the user's prior history. Drives the gold
   *  "PR" pill state in the SetRow. Not persisted to the API, the
   *  server recomputes records from scratch via /api/me/records, this
   *  is only the click-time celebratory cue. Defaults to false on
   *  every set-create path (emptySet / add_set / hydrate / pr-flash
   *  reset on failed/pending leg). Legacy persisted drafts without
   *  this field read as undefined and setStateOf treats them as
   *  plain 'done'. */
  prFlash: boolean;
  /** UI-only flag, twin of `prFlash` but for the DOWNWARD direction
   *  (next/measurement-contract.md «Il MAX dinamico: il come»). Set at the pending→done transition when the
   *  set is a qualifying MAX-regression signal (RIR 0 / fail, reps ≤ 12,
   *  e1RM ≥ δ below a stale stored MAX), drives the red ⬇️ marker in the
   *  timeline, mirror of the 🏆. Never persisted (the end-of-session
   *  prompt re-derives from the aggregate). Optional so the many
   *  set-create/hydration paths read it as undefined = false without a
   *  synchronised default bump (same pattern as `rir`/`weightSource`);
   *  cleared on the fail/unwind legs alongside prFlash. */
  regressionFlash?: boolean;
  /** Free-text note. Empty string = absent (omit-vs-zero convention).
   *  Stored on WorkoutSet.note at save. 500-char ceiling matches the
   *  schema; the textarea enforces it client-side too. */
  note: string;
  /** Per-set RPE 1..10 as a string (decimal-pad input). Empty = absent;
   *  parsed at save and dropped if not finite. */
  rpe: string;
  /** Per-set RIR (Reps in Reserve) 0..10 as a string. Empty = absent;
   *  parsed at save and dropped if not finite or out of range. RIR is
   *  the more objective fatigue indicator (RPE is perception-driven).
   *  Optional on
   *  DraftSet so the in-flight Logger UI surfaces (planStart, hydrate,
   *  add_set, etc.) don't need a synchronised string-default bump on
   *  every new field, they read as undefined and the save path
   *  treats undefined exactly like the empty string. */
  rir?: string;
  /** Bilateral toggle stamped at set-create time (1.45.0). 1 =
   *  single-implement load (one DB row), 2 = paired implements
   *  (two-DB row). Drives volume math (entryTotals + bucketTagVolumeByWeek
   *  multiply by sides). Stored per-set so toggling the entry's
   *  default mid-workout doesn't retroactively shift the volume of
   *  already-completed sets. Falls back to 2 at read time when
   *  legacy/persisted drafts don't carry it, matching SL semantics. */
  loadedSides: 1 | 2;
  /** Cardio fields (1.66.9). Populated only on cardio entries; remain
   *  empty strings on weighted/bodyweight rows. Free-text inputs same
   *  as reps/weight; buildPayload parses + validates at save.
   *  - duration: total seconds. Required for cardio sets.
   *  - distanceM: metres (gym treadmills/bikes report km on the
   *    display; we ask the user to type metres for canonical storage).
   *  - avgHrBpm: average heart rate over the set.
   *  - calories: estimate from the machine display. */
  duration: string;
  distanceM: string;
  avgHrBpm: string;
  calories: string;
  /** PRESCRIPTION: "the plan asks for as many reps as possible here".
   *  Seeded by hydrateFromPlanStarter (planStart.ts -> the program slot's
   *  `amrapLastSet` / SetPrescription.amrap lifts onto the planned set).
   *  Drives the read-only AMRAP <Badge> next to the reps label so the
   *  athlete knows the prescribed `reps` is a floor to beat, not a
   *  ceiling. DISPLAY-ONLY and NEVER persisted: a prescription is what we
   *  PROPOSE, and what we propose must never cross the border into a
   *  measured value (next/measurement-contract.md §5). Persisting it
   *  would have every plan-prescribed set claim the athlete went to the
   *  limit, a measurement invented out of an intention. The OUTCOME is
   *  `amrap` below; the two never write each other. New sets via
   *  seedNextSet default to false: once the athlete goes off-plan past
   *  the prescribed last set, the AMRAP intent doesn't auto-carry. */
  amrapPrescribed: boolean;
  /** The prescribed rep FLOOR of an AMRAP set, as text, for display only.
   *  Exists because `reps` cannot hold it: on a prescribed AMRAP the reps
   *  cell starts EMPTY (LG-28). Seeding the floor into the value is what
   *  turned the prescription into the measurement, the athlete read the
   *  prefilled 8, did 8, and confirmed "to failure" on a set that never
   *  went there (Antenore's gym smoke, 2026-07-24, session 5e0d964d).
   *  The floor is still worth SHOWING, "at least 8" is the instruction,
   *  so it moves to the placeholder, where it cannot be submitted by
   *  doing nothing. Optional: absent on every set that isn't a prescribed
   *  AMRAP, and absent on drafts persisted before this field existed, so
   *  no draft migration is needed. Never persisted, same as
   *  `amrapPrescribed` (measurement-contract §5). */
  amrapFloor?: string;
  /** PRESCRIPTION: the rep range around the prefilled target (#336 S3),
   *  shown beside the reps field as «8-12». Display only, never persisted,
   *  like `amrapFloor`. Absent on a set with no range and on drafts from
   *  before it. */
  repsRangePrescribed?: { min: number; max: number };
  /** PRESCRIPTION: how the plan BOUNDS this set in time (HI-1,
   *  interval-training.md §9.1). Seeded by hydrateFromPlanStarter from
   *  SetPrescription.work; absent on every set no plan asked for.
   *  `countdown` ⇒ the work timer runs durationSec down and the bout
   *  closes itself at zero; `countup` ⇒ the athlete's tap closes it, as
   *  a reps set does today. DISPLAY/EXECUTION ONLY and NEVER persisted:
   *  the prescription proposes, the OUTCOME is `duration` above, which
   *  the clock earns (measurement-contract §5, same border amrapPrescribed
   *  and amrap keep). seedNextSet leaves it off, past the prescribed last
   *  set the athlete is off-plan, and the bout doesn't auto-carry. */
  workPrescribed?: SetWork;
  /** PRESCRIPTION: the effort the plan asked of THIS set, as canonical RIR
   *  (0..10, AP-0a: "mai due campi"). The athlete's scale is a render-time
   *  projection, never a second stored field.
   *
   *  ⭐ This field exists because effort was the LAST prescription still
   *  writing into the measurement it is supposed to propose. `rpe`/`rir`
   *  above are the OUTCOME; the plan used to seed them directly
   *  (planStarterHydration), so a set completed WITHOUT tapping the band,
   *  the Fail leg and the self-closing bout, saved the coach's target as
   *  the athlete's judgment. On a Fail that is not merely a wrong number: it
   *  records "I had three left" on a set the athlete could not finish.
   *  Proven by prescribedEffortLeak.test.ts before this field existed.
   *
   *  Same border `amrapPrescribed`/`amrap` and `workPrescribed`/`duration`
   *  already keep (measurement-contract §5): the prescription proposes, the
   *  outcome is earned. DISPLAY ONLY and NEVER persisted, buildPayload does
   *  not carry it. `seedNextSet` leaves it off: past the prescribed last set
   *  the athlete is off-plan and the target doesn't auto-carry.
   *
   *  Also set by a REPEAT (Antenore, 2026-08-25): re-running a session
   *  carries the previous effort as a TARGET, not as something performed
   *  again. A true EDIT leaves `rpe`/`rir` alone, there they ARE the
   *  measurement. */
  rirPrescribed?: number;
  /** PRESCRIPTION: WHAT DETERMINES THE LOAD on this set, as the program's
   *  own exclusive union (PE-22 K1). `absolute` kg · `percent-of-max` ·
   *  `history` ("use the athlete's last-logged weight") · `rpe`/`rir`
   *  (effort is the axis, the athlete picks the weight) · `none`.
   *
   *  ⭐ It exists because the axis had NOWHERE to land. Every other path
   *  into the draft carries a weight that is already RESOLVED, `weight`
   *  above is a number, and from "100 kg" there is no way back to knowing
   *  whether the coach wrote 100, or 80% of a max, or "whatever you lifted
   *  last time". `planStarterHydration` is lossy for exactly this reason
   *  (PlanStarter.sets carry `weightKg` and no kind), which is why the K1
   *  adapter could not be built on it.
   *
   *  ⛔ NOT `weightSource`. That field is the PROVENANCE of a weight that
   *  has already been resolved; this one is the axis still to resolve.
   *  Confusing the two is the obvious way to get K1 wrong: `weightSource:
   *  'history'` means "the number in the cell came from history", while
   *  `intensityPrescribed: {kind:'history'}` means "no number was written,
   *  go look it up".
   *
   *  ⛔ The axis is EXCLUSIVE, a set prescribed at 80% cannot also be
   *  prescribed at RIR 2, because the two contradict each other the moment
   *  the athlete finishes 80% at RIR 4, and neither would be right
   *  (program-editor-redesign.md, the K1 mockup). The union enforces it by
   *  construction. ⚠️ Orthogonal to how the set is BOUNDED (`workPrescribed`,
   *  `amrapPrescribed`): AMRAP says when you stop, not how much you load,
   *  which is why the two can coexist and effort-vs-weight cannot.
   *
   *  Same discipline as `workPrescribed`/`amrapPrescribed`/`rirPrescribed`:
   *  DISPLAY ONLY and NEVER persisted, buildPayload does not carry it, and
   *  the editor's writes go back through the composer's `updateCell`, never
   *  through a DraftSet. Optional, so drafts persisted before this field
   *  need no migration. */
  intensityPrescribed?: SetIntensity;
  /** PRESCRIPTION: the rest the plan wrote after THIS set, seconds
   *  (SetPrescription.restSec). Absent ⇒ the rest ladder decides, which is
   *  what every set does today; 0 is a real value ("straight into the next
   *  bout") and is honoured, so readers must test for undefined and not
   *  for truthiness. Never persisted: a rest is not a measurement. */
  restPrescribedSec?: number;
  /** PRESCRIPTION: the coach's per-set note (SetRowDraft.notesPerCell at
   *  this week), K3.4. Same discipline as the mirrors above: DISPLAY ONLY,
   *  never persisted (buildPayload does not carry it, it reads the program
   *  Draft, not a DraftSet), and absent means "nothing written", not "note
   *  cleared". The "Nota" chip's selected state reads this rather than
   *  re-deriving it, so the read half and the write half (the row's own
   *  `notesPerCell[weekIdx]`, via updateSetRowNote) can never disagree. */
  notePrescribed?: string;
  /** OUTCOME: "on THIS set I went to the limit on the open variable".
   *  Declared by the athlete, and ONLY by the athlete: the FocusPanel
   *  AMRAP chip is the single writer (update_set patch). Shares its
   *  nature with `fail`, an execution outcome, not structure, so it
   *  hydrates and persists exactly like it: buildPayload carries it onto
   *  the saved set (-> LogSet.amrap, omit-the-default: only `true`
   *  travels) and edit hydration reads it back, while repeat / the
   *  history copy blank it. Independent of `amrapPrescribed`: the athlete
   *  can mark a set no plan asked for, and can leave a prescribed-AMRAP
   *  set unmarked because they stopped short. */
  amrap: boolean;
  /** Provenance of the prefilled weight (Phase 1, default-weight chain).
   *  Stamped by hydrateFromPlanStarter via resolveDefaultWeight; flipped
   *  to 'manual' the first time the athlete edits the weight cell.
   *  Forwarded into LogSet.weightSource at save. Absent on legacy/persisted
   *  drafts; absent = treat as 'manual' (those rows predate the field and
   *  were all athlete-typed).
   *  ⚠️ No longer "data-only, no UI cue": since PG-9 the three
   *  `effort-from-*` values ARE rendered, FocusPanelInputs marks the weight
   *  label and explains, in the bubble, that the number is a PROPOSAL and
   *  which instrument produced it. The other values stay data-only. */
  weightSource?: WeightSource;
  /** Dropset chain attached to this set (1.80.0). null = no chain
   *  (default). The SetRow exposes an "Add dropset chain" affordance
   *  on weighted + bodyweight parents only; cardio rows live in
   *  CardioSetRow and skip this field entirely. Persisted into
   *  WorkoutSet.dropset at save via buildPayload's per-step parse +
   *  toCanonicalKg conversion (mirrors web's `builtDropset` in
   *  WorkoutLog.tsx:1112). Mirrors web SetDraft.dropset (line ~99). */
  dropset: DropsetDraft | null;
}

export const setStateOf = (s: DraftSet): SetState => {
  if (s.completedAt === null) return 'pending';
  if (s.fail) return 'failed';
  return s.prFlash ? 'pr' : 'done';
};
